import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Download, Upload, Layers, FolderKanban, FileText, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import {
  JF_ISSUE_TYPE, JF_ISSUE_KEY, JF_ISSUE_ID, JF_SUMMARY, JF_STATUS,
  JF_DUE_DATE, JF_LABELS, JF_LABELS_1, JF_LABELS_2, JF_LABELS_3, JF_LABELS_4,
  JF_PARENT, JF_PARENT_KEY, JF_PARENT_SUM,
  JF_EPIC_LINK, JF_EPIC_STATUS, JF_SP,
  JF_TGT_START, JF_TGT_END, JF_PLAN_START, JF_PLAN_END,
  JF_SPRINT, JF_TEAM, JF_CONTRIB_TEAM, JF_PROGRAM, JF_RELEASE, JF_DEP_SUMMARY, JF_IT_POV,
  JF_IT_RAG, JF_IT_PM, JF_TIER, JF_RANK, JF_WORKSTREAM,
} from '../lib/jiraFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ icon: Icon, iconColor, title, description, csvFields, exportUrl, importUrl, importLabel, exportFilename, hint }) => {
  const [importing, setImporting] = useState(false);

  const handleExport = async () => {
    try {
      const res = await axios.get(exportUrl, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = exportFilename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${exportFilename} downloaded`);
    } catch (e) {
      console.error('Export failed', e);
      toast.error('Export failed — check your connection and try again');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!globalThis.confirm(`This will replace ALL ${title.toLowerCase()} with data from "${file.name}". Continue?`)) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(importUrl, form);
      toast.success(`Imported ${res.data.imported} ${title.toLowerCase()}`);
    } catch (e) {
      console.error('Import failed', e);
      const detail = e?.response?.data?.detail;
      toast.error(detail ? `Import failed: ${detail}` : 'Import failed — check the file format and try again');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 ${iconColor} rounded-xl flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{description}</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-teal-500 hover:bg-teal-400 text-white text-sm font-medium transition-colors"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
        <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-sm font-medium cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50 ${importing ? 'opacity-50 pointer-events-none' : ''}`}>
          <Upload className="w-4 h-4" />
          {importing ? 'Importing…' : `Import CSV`}
          <input type="file" accept=".csv" className="hidden" onChange={handleImport} disabled={importing} />
        </label>
      </div>

      {/* CSV field reference */}
      <div className="bg-slate-50 dark:bg-slate-900/40 rounded-xl p-4 space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
          <FileText className="w-3.5 h-3.5" /> {importLabel} CSV columns
        </div>
        <div className="flex flex-wrap gap-1.5">
          {csvFields.map(f => (
            <span key={f} className="px-2 py-0.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-600 dark:text-slate-300 font-mono">
              {f}
            </span>
          ))}
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-500 pt-1">
          {hint} Export first to see the exact format, then fill in your JIRA data and re-import.
        </p>
      </div>
    </div>
  );
};

Section.propTypes = {
  icon: PropTypes.elementType.isRequired,
  iconColor: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  csvFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  exportUrl: PropTypes.string.isRequired,
  importUrl: PropTypes.string.isRequired,
  importLabel: PropTypes.string.isRequired,
  exportFilename: PropTypes.string.isRequired,
  hint: PropTypes.string,
};

const PROGRAM_FIELDS = [
  'key', 'name', 'jira_rank', 'tier', 'rag',
  'technical_project_manager', 'product_lead', 'business_sponsor', 'it_pov',
  'rollup_team_contributing', 'start_date', 'go_live_date',
  'current_status', 'risk_summary', 'next_steps', 'dependency_ai_summary',
];

const PROJECT_FIELDS = [
  JF_ISSUE_TYPE, JF_ISSUE_KEY, JF_ISSUE_ID, JF_SUMMARY, JF_STATUS,
  JF_DUE_DATE, JF_LABELS, JF_LABELS_1, JF_LABELS_2, JF_LABELS_3, JF_LABELS_4,
  JF_PARENT, JF_PARENT_KEY, JF_PARENT_SUM,
  JF_EPIC_LINK, JF_EPIC_STATUS, JF_SP,
  JF_TGT_START, JF_TGT_END, JF_PLAN_START, JF_PLAN_END,
  JF_SPRINT, JF_TEAM, JF_CONTRIB_TEAM, JF_PROGRAM, JF_RELEASE, JF_DEP_SUMMARY, JF_IT_POV,
  JF_IT_RAG, JF_IT_PM, JF_TIER, JF_RANK, JF_WORKSTREAM,
];

const COMPLETED_FIELDS = PROJECT_FIELDS;

const UploadDownloadPage = () => (
  <div className="p-6 lg:p-8 space-y-6">
    {/* Page header */}
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-xl flex items-center justify-center">
        <Download className="w-6 h-6 text-teal-600 dark:text-teal-400" />
      </div>
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Upload / Download</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">Export data for editing or import real data from JIRA</p>
      </div>
    </div>

    <Section
      icon={Layers}
      iconColor="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400"
      title="Programs"
      description="Export all programs to CSV or replace them by importing a JIRA-mapped file"
      csvFields={PROGRAM_FIELDS}
      exportUrl={`${API}/executive/programs/export`}
      importUrl={`${API}/executive/programs/import`}
      importLabel="Programs"
      exportFilename="programs.csv"
      hint="For rag use: Red, Amber, or Green."
    />

    <Section
      icon={FolderKanban}
      iconColor="bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
      title="Projects"
      description="Export all projects (epics) to CSV or replace them by importing a JIRA-mapped file"
      csvFields={PROJECT_FIELDS}
      exportUrl={`${API}/executive/projects/export`}
      importUrl={`${API}/executive/projects/import`}
      importLabel="Projects"
      exportFilename="projects.csv"
      hint="Matches the JIRA CSV export format — import your JIRA file directly without renaming columns."
    />

    <Section
      icon={CheckCheck}
      iconColor="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
      title="Completed Projects"
      description="Projects that went live in the last 30 days — shown in the Go-Live Calendar completed section"
      csvFields={COMPLETED_FIELDS}
      exportUrl={`${API}/executive/completed-projects/export`}
      importUrl={`${API}/executive/completed-projects/import`}
      importLabel="Completed Projects"
      exportFilename="completed-projects.csv"
      hint="Same format as the Projects import — use your JIRA CSV export directly. The Due date column is treated as the actual go-live date."
    />
  </div>
);

export default UploadDownloadPage;
